import React, { useState } from 'react';
import { User } from '../types';
import { updateUserProfile } from '../services/dataService';
import { CATEGORIES } from '../constants';

interface UserProfileProps {
   user: User;
   onUpdateUser: (updatedUser: User) => void;
   onBack: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ user, onUpdateUser, onBack }) => {
   const [name, setName] = useState(user.name || '');
   const [phone, setPhone] = useState(user.phone || '');
   const [targetExam, setTargetExam] = useState(user.targetExam || '');
   const [saving, setSaving] = useState(false);
   const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

   const handleSave = async (e: React.FormEvent) => {
      e.preventDefault();
      setSaving(true);
      setMsg(null);

      const success = await updateUserProfile(user.id, {
         name,
         phone,
         targetExam,
         email: user.email
      });

      if (success) {
         setMsg({ type: 'success', text: 'Profile updated successfully!' });
         onUpdateUser({ ...user, name, phone, targetExam });
      } else {
         setMsg({ type: 'error', text: 'Failed to update profile. Please try again.' });
      }
      setSaving(false);
   };

   const getTierColor = (tier?: string) => {
      switch (tier) {
         case 'premium': return 'bg-gradient-to-r from-purple-600 to-blue-600 text-white';
         case 'pro': return 'bg-orange-500 text-white';
         default: return 'bg-slate-100 text-slate-600';
      }
   };

   return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
         <div className="max-w-2xl mx-auto">
            <button
               onClick={onBack}
               className="mb-8 flex items-center text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
            >
               <i className="fas fa-arrow-left mr-2"></i> Back to Dashboard
            </button>

            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
               {/* Header Banner */}
               <div className="bg-slate-900 h-32 relative">
                  <div className="absolute -bottom-12 left-8">
                     <div className="w-24 h-24 rounded-full border-4 border-white dark:border-slate-800 bg-orange-100 dark:bg-orange-950 text-orange-600 dark:text-orange-400 flex items-center justify-center text-3xl font-bold">
                        {name.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
                     </div>
                  </div>
               </div>

               <div className="pt-16 px-8 pb-8">
                  <div className="flex justify-between items-start mb-8">
                     <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{user.name || 'Aspirant'}</h1>
                        <p className="text-slate-500 dark:text-slate-400">{user.email}</p>
                     </div>
                     <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${getTierColor(user.subscriptionTier)}`}>
                        {user.subscriptionTier || 'Free'} Plan
                     </span>
                  </div>

                  <form onSubmit={handleSave} className="space-y-6">
                     <div>
                        <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4 border-b border-slate-100 dark:border-slate-700 pb-2">Account Details</h3>
                        <div className="grid grid-cols-1 gap-6">
                           <div>
                              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Full Name</label>
                              <input
                                 type="text"
                                 value={name}
                                 onChange={(e) => setName(e.target.value)}
                                 className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                                 placeholder="Enter your full name"
                              />
                           </div>
                           <div>
                              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Email Address</label>
                              <input
                                 type="email"
                                 value={user.email}
                                 disabled
                                 className="w-full p-2 border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 text-slate-500 rounded cursor-not-allowed"
                              />
                              <p className="text-xs text-slate-400 mt-1">Email cannot be changed.</p>
                           </div>

                           {/* Phone Number */}
                           <div>
                              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                 <i className="fas fa-phone mr-2 text-orange-500"></i>Phone Number
                              </label>
                              <input
                                 type="tel"
                                 value={phone}
                                 onChange={(e) => setPhone(e.target.value)}
                                 className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                                 placeholder="+91 98765 43210"
                              />
                           </div>

                           {/* Target Exam Category */}
                           <div>
                              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                 <i className="fas fa-graduation-cap mr-2 text-orange-500"></i>Target Exam Category
                              </label>
                              <select
                                 value={targetExam}
                                 onChange={(e) => setTargetExam(e.target.value)}
                                 className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                              >
                                 <option value="">Select Exam Category</option>
                                 {CATEGORIES.map(cat => (
                                    <option key={cat.id} value={cat.name}>{cat.name}</option>
                                 ))}
                              </select>
                              {targetExam && (
                                 <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                    <i className="fas fa-check-circle mr-1"></i>
                                    You're preparing for: {targetExam}
                                 </p>
                              )}
                           </div>
                        </div>
                     </div>

                     <div>
                        <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4 border-b border-slate-100 dark:border-slate-700 pb-2">Subscription</h3>
                        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                           <div className="flex justify-between items-center mb-2">
                              <span className="font-medium text-slate-700 dark:text-slate-300">Current Plan</span>
                              <span className="font-bold text-slate-900 dark:text-white capitalize">{user.subscriptionTier || 'Free'}</span>
                           </div>
                           <div className="flex justify-between items-center">
                              <span className="text-sm text-slate-500 dark:text-slate-400">Status</span>
                              <span className="text-sm text-green-600 dark:text-green-400 font-medium">Active</span>
                           </div>
                           {user.subscriptionExpiry && (
                              <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                                 <span className="text-sm text-slate-500 dark:text-slate-400">Expires On</span>
                                 <span className="text-sm text-slate-700 dark:text-slate-300">{new Date(user.subscriptionExpiry).toLocaleDateString()}</span>
                              </div>
                           )}
                        </div>
                     </div>

                     {msg && (
                        <div className={`p-3 rounded-lg text-sm border ${msg.type === 'success' 
                           ? 'bg-green-50 dark:bg-green-950/20 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800/30' 
                           : 'bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800/30'}`}>
                           {msg.text}
                        </div>
                     )}

                     <div className="pt-4 flex justify-end">
                        <button
                           type="submit"
                           disabled={saving}
                           className="bg-slate-900 dark:bg-orange-500 text-white px-6 py-2 rounded hover:bg-slate-800 dark:hover:bg-orange-600 disabled:opacity-70 transition-colors font-medium"
                        >
                           {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                     </div>
                  </form>
               </div>
            </div>
         </div>
      </div>
   );
};

export default UserProfile;